import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useContext, useEffect, useState } from "react";
import { FaShieldAlt, FaTruck, FaUndo } from "react-icons/fa";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import SummaryApi from "../common";
import CategroyWiseProductDisplay from "../components/CategoryWiseProductDisplay";
import ProductReviews from "../components/ProductReviews";
import ReviewForm from "../components/ReviewForm";
import SEO from "../components/SEO";
import StarRating from "../components/StarRating";
import WishlistButton from "../components/WishlistButton";
import {
  PRODUCT_ADD_TO_CART,
  PRODUCT_BUY_NOW,
  PRODUCT_TOTAL_PRICE_LABEL,
  ROUTES,
} from "../constants";
import Context from "../context";
import addToCart from "../helpers/addToCart";
import displayNPRCurrency from "../helpers/displayCurrency";
import { request } from "../helpers/request";

const ProductDetails = () => {
  const [data, setData] = useState({
    productName: "",
    brandName: "",
    category: "",
    productImage: [],
    description: "",
    price: "",
    sellingPrice: "",
  });
  const [selectedVariant, setSelectedVariant] = useState(null);
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState("");
  const [zoomImageCoordinate, setZoomImageCoordinate] = useState({
    x: 0,
    y: 0,
  });
  const [zoomImage, setZoomImage] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const { fetchUserAddToCart } = useContext(Context);
  const { user, isLoading: userLoading } = useSelector((state) => state?.user);
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);

  const fetchProductDetails = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const response = await request(SummaryApi.productDetails, {
        body: JSON.stringify({ productId: params?.id }),
      });
      const dataResponse = await response.json();

      if (
        response.status === 404 ||
        !dataResponse.success ||
        !dataResponse.data
      ) {
        setNotFound(true);
        return;
      }

      if (dataResponse.success && dataResponse.data) {
        setData(dataResponse.data);
        setActiveImage(dataResponse.data.productImage?.[0] || "");
        if (
          dataResponse.data.hasVariants &&
          dataResponse.data.variants?.length > 0
        ) {
          setSelectedVariant(dataResponse.data.variants[0]);
        } else {
          setSelectedVariant(null);
        }
      }
    } catch (error) {
      setNotFound(true);
      console.error("Error fetching product details:", error);
    } finally {
      setLoading(false);
    }
  }, [params?.id]);

  useEffect(() => {
    fetchProductDetails();
    window.scrollTo(0, 0);

    const handleScroll = () => {
      if (window.scrollY > 600) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [fetchProductDetails]);

  const handleZoomImage = useCallback((e) => {
    setZoomImage(true);
    const { left, top, width, height } =
      e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - left) / width;
    const y = (e.clientY - top) / height;
    setZoomImageCoordinate({ x, y });
  }, []);

  const handleAddToCart = async (e, id) => {
    await addToCart(e, id, selectedVariant?._id);
    fetchUserAddToCart();
  };

  const handleBuyProduct = async (e, id) => {
    await addToCart(e, id, selectedVariant?._id);
    await fetchUserAddToCart();
    navigate("/cart");
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-24 animate-pulse">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="w-full lg:w-1/2 aspect-square bg-surface-muted rounded-card" />
          <div className="w-full lg:w-1/2 space-y-6">
            <div className="h-4 bg-surface-muted w-24 rounded" />
            <div className="h-12 bg-surface-muted w-3/4 rounded" />
            <div className="h-8 bg-surface-muted w-1/4 rounded" />
            <div className="h-32 bg-surface-muted w-full rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="container mx-auto px-6 py-20 md:py-28">
        <div className="max-w-2xl mx-auto bg-surface-muted rounded-card border border-surface-border p-10 md:p-14 text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.25em] text-accent mb-4">
            Product Unavailable
          </p>
          <h1 className="text-3xl md:text-4xl font-black text-primary tracking-tight mb-4">
            This product could not be found.
          </h1>
          <p className="text-sm md:text-base text-neutral-500 max-w-lg mx-auto mb-8">
            The link is invalid, the product was removed, or it is no longer
            available.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link
              to={ROUTES.HOME}
              className="btn-primary inline-flex items-center gap-2"
            >
              <FaArrowLeftLong />
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title={data?.productName}
        description={data?.description}
        image={data?.productImage?.[0]}
        type="product"
      />
      {/* Desktop Floating Bar (Legacy) - Hidden on Mobile */}
      <div
        className={`fixed top-20 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-surface-border py-4 hidden lg:flex transition-all duration-500 ${scrolled ? "translate-y-0 opacity-100" : "translate-y-[-100%] opacity-0"}`}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src={activeImage}
              className="w-12 h-12 object-contain bg-surface-muted rounded"
              alt="Mini"
            />
            <div>
              <h3 className="font-bold text-sm text-primary">
                {data?.productName || "Product Name"}
              </h3>
              <p className="text-xs font-black text-accent">
                {displayNPRCurrency(
                  selectedVariant
                    ? selectedVariant.sellingPrice
                    : data?.sellingPrice || 0,
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <button
              onClick={(e) => handleAddToCart(e, data?._id)}
              className="px-6 py-2 bg-primary text-white text-xs font-black rounded-full hover:bg-accent transition-all"
            >
              {PRODUCT_ADD_TO_CART}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-surface-border p-3 z-[110] lg:hidden flex items-center gap-3 animate-slide-up pb-safe shadow-[0_-8px_24px_rgba(0,0,0,0.06)]">
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-black uppercase tracking-widest text-neutral-400">
            {PRODUCT_TOTAL_PRICE_LABEL}
          </p>
          <p className="text-lg font-black text-primary leading-tight">
            {displayNPRCurrency(
              selectedVariant
                ? selectedVariant.sellingPrice
                : data?.sellingPrice || 0,
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <WishlistButton
            productId={params?.id}
            className="w-11 h-11 flex items-center justify-center bg-surface-muted rounded-xl text-lg border border-surface-border"
          />
        </div>
        <div className="flex gap-2">
          {user?._id ? (
            <>
              <button
                onClick={(e) => handleAddToCart(e, data?._id)}
                className="h-11 px-4 bg-white border-2 border-primary text-primary font-black text-xs rounded-xl active:scale-95 transition-all"
              >
                {PRODUCT_ADD_TO_CART}
              </button>
              <button
                onClick={(e) => handleBuyProduct(e, data?._id)}
                className="h-11 px-5 bg-primary text-white font-black text-xs rounded-xl active:scale-95 transition-all shadow-button-primary"
              >
                {PRODUCT_BUY_NOW}
              </button>
            </>
          ) : (
            <Link
              to={ROUTES.LOGIN}
              className="h-11 px-6 bg-primary text-white flex items-center justify-center font-black text-sm rounded-xl"
            >
              Sign In to Order
            </Link>
          )}
        </div>
      </div>

      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12 lg:py-20">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Left Column: Images */}
          <div className="w-full lg:w-[55%] space-y-4 md:space-y-6">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6">
              {/* Thumbnails */}
              <div className="flex md:flex-col gap-3 overflow-x-auto md:overflow-y-auto scrollbar-none pb-2 md:pb-0">
                {data?.productImage?.map((imgURL) => (
                  <button
                    key={imgURL}
                    onClick={() => setActiveImage(imgURL)}
                    className={`w-16 h-16 md:w-20 md:h-20 flex-shrink-0 bg-surface-muted rounded-xl p-2 border-2 transition-all ${activeImage === imgURL ? "border-accent shadow-premium" : "border-transparent hover:border-gray-200"}`}
                  >
                    <img
                      src={imgURL}
                      className="w-full h-full object-contain mix-blend-multiply"
                      alt="Thumbnail"
                    />
                  </button>
                ))}
              </div>

              {/* Active Image */}
              <div
                className="flex-1 relative aspect-square bg-surface-muted rounded-3xl md:rounded-card overflow-hidden cursor-crosshair group shadow-sm"
                onMouseMove={handleZoomImage}
                onMouseLeave={() => setZoomImage(false)}
              >
                <motion.img
                  key={activeImage}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  src={activeImage}
                  className="w-full h-full object-contain mix-blend-multiply p-8 md:p-12"
                  alt="Product"
                />

                {/* Zoom Overlay (Hidden on Mobile) */}
                <AnimatePresence>
                  {zoomImage && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 pointer-events-none hidden lg:block"
                      style={{
                        backgroundImage: `url(${activeImage})`,
                        backgroundPosition: `${zoomImageCoordinate.x * 100}% ${zoomImageCoordinate.y * 100}%`,
                        backgroundSize: "250%",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Right Column: Info */}
          <div className="w-full lg:w-[45%] flex flex-col">
            <div className="space-y-6 md:space-y-8">
              <div className="space-y-2 md:space-y-4">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-accent">
                  {data?.brandName || "Premium Series"}
                </p>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-primary tracking-tight leading-tight">
                  {data?.productName || "Product Loading..."}
                </h1>
                <div className="flex items-center gap-4">
                  <StarRating
                    rating={data?.averageRating || 0}
                    size="sm"
                    showNumber
                  />
                  <span className="text-[10px] md:text-xs font-bold text-gray-400 upper-case tracking-widest">
                    ({data?.reviewCount || 0} Reviews)
                  </span>
                </div>
              </div>

              <div className="flex items-baseline gap-4 flex-wrap">
                {/* Stock Indicator */}
                {(() => {
                  const stock = selectedVariant
                    ? selectedVariant.stock
                    : data?.stock;
                  if (stock === 0)
                    return (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-500 border border-red-100">
                        Out of Stock
                      </span>
                    );
                  if (stock <= 5 && stock > 0)
                    return (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-amber-50 text-amber-600 border border-amber-100">
                        Only {stock} left
                      </span>
                    );
                  return (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-600 border border-green-100">
                      In Stock
                    </span>
                  );
                })()}
                <span className="text-3xl md:text-4xl font-black text-primary tracking-tighter">
                  {displayNPRCurrency(
                    selectedVariant
                      ? selectedVariant.sellingPrice
                      : data?.sellingPrice || 0,
                  )}
                </span>
                {(selectedVariant
                  ? selectedVariant.price > selectedVariant.sellingPrice
                  : data?.price > data?.sellingPrice) && (
                  <>
                    <span className="text-lg md:text-xl text-gray-300 line-through font-bold">
                      {displayNPRCurrency(
                        selectedVariant ? selectedVariant.price : data?.price,
                      )}
                    </span>
                    <span className="bg-accent/10 text-accent text-[10px] md:text-xs font-black px-3 py-1 rounded-full uppercase">
                      Save{" "}
                      {Math.round(
                        (((selectedVariant
                          ? selectedVariant.price
                          : data.price) -
                          (selectedVariant
                            ? selectedVariant.sellingPrice
                            : data.sellingPrice)) /
                          (selectedVariant
                            ? selectedVariant.price
                            : data.price)) *
                          100,
                      )}
                      %
                    </span>
                  </>
                )}
              </div>

              {/* Variant Selection */}
              {data.hasVariants && data.variants?.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Select Variant
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {data.variants.map((v) => (
                      <button
                        key={v._id}
                        onClick={() => setSelectedVariant(v)}
                        className={`px-4 py-2 rounded-full border-2 text-xs font-bold transition-all ${selectedVariant?._id === v._id ? "border-accent bg-accent/5 text-accent" : "border-gray-100 text-gray-400 hover:border-gray-200"}`}
                      >
                        {v.size && `Size: ${v.size}`}
                        {v.size && v.color && " - "}
                        {v.color && `Color: ${v.color}`}
                        {!v.size && !v.color && (v.sku || "Variant")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-b border-surface-border py-6 md:py-8 space-y-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Product Description
                </h4>
                <p className="text-sm md:text-base text-gray-500 leading-relaxed font-medium">
                  {data?.description || "No description available."}
                </p>
              </div>

              {/* Perks */}
              <div className="grid grid-cols-3 gap-3 md:gap-4">
                <div className="flex flex-col items-center p-3 md:p-4 bg-surface-muted rounded-2xl text-center">
                  <FaShieldAlt className="text-primary mb-2 text-lg" />
                  <span className="text-[9px] md:text-[10px] font-black uppercase tracking-wider text-primary">
                    Warranty
                  </span>
                </div>
                <div className="flex flex-col items-center p-3 md:p-4 bg-surface-muted rounded-2xl text-center">
                  <FaTruck className="text-primary mb-2 text-lg" />
                  <span className="text-[9px] md:text-[10px] font-black uppercase tracking-wider text-primary">
                    Free Ship
                  </span>
                </div>
                <div className="flex flex-col items-center p-3 md:p-4 bg-surface-muted rounded-2xl text-center">
                  <FaUndo className="text-primary mb-2 text-lg" />
                  <span className="text-[9px] md:text-[10px] font-black uppercase tracking-wider text-primary">
                    Easy Return
                  </span>
                </div>
              </div>

              {/* Actions - Hidden on mobile as we have the sticky bottom bar */}
              <div className="hidden lg:flex flex-col pt-6 md:pt-8 space-y-4">
                {!userLoading &&
                  (user?._id ? (
                    <div className="flex flex-col gap-4">
                      {(() => {
                        const stock = selectedVariant
                          ? selectedVariant.stock
                          : data?.stock;
                        const isOOS = stock === 0;
                        return (
                          <>
                            <button
                              onClick={(e) => handleBuyProduct(e, data?._id)}
                              disabled={isOOS}
                              className="w-full h-14 md:h-16 bg-primary text-white font-black text-lg rounded-full hover:bg-accent transition-all shadow-xl shadow-primary/20 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {isOOS ? "Out of Stock" : "Buy Now"}
                            </button>
                            <div className="flex items-center gap-4">
                              <button
                                onClick={(e) => handleAddToCart(e, data?._id)}
                                disabled={isOOS}
                                className="flex-1 h-14 md:h-16 border-2 border-primary text-primary font-black text-lg rounded-full hover:bg-primary hover:text-white transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Add to Cart
                              </button>
                              <WishlistButton
                                productId={data?._id}
                                className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-surface-muted rounded-full text-2xl"
                              />
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <Link
                      to={ROUTES.LOGIN}
                      className="w-full h-14 md:h-16 bg-primary text-white text-center flex items-center justify-center font-black text-lg rounded-full shadow-xl shadow-primary/20"
                    >
                      Sign in to Order
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16 md:mt-24 border-t border-surface-border pt-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-primary tracking-tight">
                Customer Reviews
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                Read what other customers think about this product
              </p>
            </div>
            {user?._id && (
              <button
                onClick={() => setShowReviewForm(true)}
                className="px-6 py-3 bg-primary text-white font-black text-sm rounded-full hover:bg-accent transition-all shadow-lg shadow-primary/10"
              >
                Write a Review
              </button>
            )}
          </div>

          {showReviewForm && (
            <div className="mb-12">
              <ReviewForm
                productId={params?.id}
                onReviewSubmitted={() => {
                  setShowReviewForm(false);
                  fetchProductDetails();
                }}
                onCancel={() => setShowReviewForm(false)}
              />
            </div>
          )}

          <ProductReviews productId={params?.id} />
        </div>
      </main>

      {/* Recommendations */}
      <div className="bg-surface-muted pt-12 pb-32 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          {data.category && (
            <CategroyWiseProductDisplay
              category={data?.category}
              heading={"You Might Also Like"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
